'use client';

import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import {
  pollingIntervalAtom,
  changeCheckResultAtom,
  pollingDegradedAtom,
} from '@/store/polling-atoms';
import { userAtom } from '@/store';
import { getChangeCheck } from '@/services/change-check';

/**
 * 统一轮询组件
 * Unified polling component
 *
 * 使用 React Query 轮询 /message/v1/change-check 接口
 * Uses React Query to poll /message/v1/change-check endpoint
 *
 * 特性：
 * Features:
 * - 仅在用户登录时启用 Only enabled when user is logged in
 * - 动态间隔（响应 pollingIntervalAtom 变化）Dynamic interval (responds to pollingIntervalAtom)
 * - 页面隐藏时自动暂停 Auto pause when page is hidden
 * - 失败降级策略（3次失败后延长到30秒）Failure degradation (extend to 30s after 3 failures)
 */
export function ChangeCheckPoller() {
  const [interval, setInterval] = useAtom(pollingIntervalAtom);
  const setChangeCheckResult = useSetAtom(changeCheckResultAtom);
  const setDegraded = useSetAtom(pollingDegradedAtom);
  const user = useAtomValue(userAtom);
  const failureCountRef = useRef(0);
  const savedIntervalRef = useRef(interval); // 保存降级前的间隔 (Save interval before degradation)

  const { data, error } = useQuery({
    queryKey: ['change-check'],
    queryFn: () => getChangeCheck(true),
    enabled: !!user?.accessToken, // 仅在用户登录时启用 (Only enabled when user is logged in)
    refetchInterval: interval, // 动态间隔（响应 atom 变化）Dynamic interval (responds to atom changes)
    refetchIntervalInBackground: false, // 页面隐藏时自动暂停 (Auto pause when page is hidden)
    refetchOnWindowFocus: true, // 页面恢复时自动请求 (Auto request when page is focused)
    retry: false, // 不重试，失败直接进入降级逻辑 (No retry, go directly to degradation on failure)
    structuralSharing: false, // 禁用结构共享，确保每次响应都是新引用，触发 useEffect (Disable structural sharing to ensure every response is a new reference, triggering useEffect)
  });

  // 处理成功 (Handle success)
  useEffect(() => {
    if (data) {
      // 更新 change-check 结果到 atom（自动通知所有订阅者）
      // Update change-check result to atom (automatically notifies all subscribers)
      setChangeCheckResult(data);

      // 成功后重置失败计数 (Reset failure count on success)
      if (failureCountRef.current > 0) {
        console.log('[ChangeCheckPoller] Recovery from failures');
        failureCountRef.current = 0;

        // 如果当前是降级状态，恢复正常间隔
        // If currently degraded, restore normal interval
        if (interval === 30000) {
          setInterval(savedIntervalRef.current);
          setDegraded(false);
        }
      }
    }
  }, [data, interval, setChangeCheckResult, setInterval, setDegraded]);

  // 处理失败（仅打印警告，不引起页面错误）
  // Handle failure (warn only, no page errors)
  useEffect(() => {
    if (error) {
      failureCountRef.current++;
      console.warn('[ChangeCheckPoller] Request failed', error);

      // 每次失败立即触发全量订阅者（保证不漏更新）
      // Trigger all subscribers on every failure (ensure no updates are missed)
      setChangeCheckResult({
        notice: 1,
        balance: 1,
        colorball: 1,
        reward: 1,
        gullak: 1,
      });

      if (failureCountRef.current >= 3 && interval !== 30000) {
        // 连续失败3次：延长间隔到30秒
        // 3 consecutive failures: extend interval to 30s
        console.warn('[ChangeCheckPoller] 3 failures, extending interval to 30s');
        savedIntervalRef.current = interval; // 保存当前间隔 (Save current interval)
        setInterval(30000);
        setDegraded(true);
      }
    }
  }, [error, interval, setChangeCheckResult, setInterval, setDegraded]);

  return null;
}
